import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, HelpCircle } from "lucide-react";
import Logo from "@/components/portal/Logo";
import SampleInvoiceModal from "@/components/portal/SampleInvoiceModal";
import { getInvoiceValidationState } from "@/lib/invoice";

/**
 * Dedicated Invoice Number entry page — sits between the landing page
 * (`/`) and the fulfilment chooser (`/select`). Once a valid invoice is
 * entered, we forward it via router state through `/select` all the way
 * to `/pickup` and `/delivery`, so the customer is never asked for it
 * again.
 *
 * Validation: exactly four numeric digits from 1000–2999.
 */
export default function InvoiceEntry() {
  const navigate = useNavigate();
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [invalidEntry, setInvalidEntry] = useState(false);
  const [touched, setTouched] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [sampleOpen, setSampleOpen] = useState(false);
  const digitRefs = useRef([]);

  const validationState = useMemo(
    () => getInvoiceValidationState(digits, invalidEntry, touched),
    [digits, invalidEntry, touched],
  );
  const { invoice, groupType, invoiceValid, showError } = validationState;
  const errorMessage = invoiceValid
    ? null
    : "Please enter a valid 4-digit stitching invoice number";

  const focusDigit = (index) => {
    digitRefs.current[index]?.focus();
  };

  const setDigit = (index, value) => {
    setDigits((current) => current.map((digit, digitIndex) => (
      digitIndex === index ? value : digit
    )));
  };

  const applyPastedDigits = (rawValue, startIndex) => {
    if (!/^\d{1,4}$/.test(rawValue) || rawValue.length > 4 - startIndex) {
      setInvalidEntry(true);
      setTouched(true);
      return;
    }

    setDigits((current) => {
      const next = [...current];
      rawValue.split("").forEach((digit, offset) => {
        next[startIndex + offset] = digit;
      });
      return next;
    });
    setInvalidEntry(false);
    focusDigit(Math.min(startIndex + rawValue.length, 3));
  };

  const handleDigitChange = (event, index) => {
    const rawValue = event.target.value;

    if (rawValue === "") {
      setDigit(index, "");
      setInvalidEntry(false);
      return;
    }

    if (rawValue.length > 1) {
      applyPastedDigits(rawValue, index);
      return;
    }

    if (!/^\d$/.test(rawValue)) {
      setInvalidEntry(true);
      setTouched(true);
      return;
    }

    setDigit(index, rawValue);
    setInvalidEntry(false);
    if (index < 3) focusDigit(index + 1);
  };

  const handleDigitKeyDown = (event, index) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      event.preventDefault();
      setDigit(index - 1, "");
      focusDigit(index - 1);
    }

    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      focusDigit(index - 1);
    }

    if (event.key === "ArrowRight" && index < 3) {
      event.preventDefault();
      focusDigit(index + 1);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (!invoiceValid) return;
    // The strict validator is the only source for the customer-side route.
    const target = groupType === "A" ? "/group-a" : "/group-b";
    navigate(target, { state: { invoice_number: invoice, group_type: groupType } });
  };

  return (
    <div className="min-h-screen bg-ivory flex flex-col">
      <header className="border-b border-line bg-ivory/70 backdrop-blur">
        <div className="max-w-2xl mx-auto flex items-center justify-between px-5 sm:px-8 py-5">
          <Logo />
          <button
            data-testid="invoice-back-btn"
            onClick={() => navigate("/")}
            className="text-xs text-muted2 hover:text-maroon inline-flex items-center gap-1.5
              transition-colors"
          >
            <ArrowLeft size={13} strokeWidth={1.6} /> Back
          </button>
        </div>
      </header>

      <main className="flex-1 flex items-start justify-center px-5 sm:px-8 py-10 sm:py-16">
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-lg"
        >
          <div className="text-center mb-8 sm:mb-10">
            <p className="overline text-[10px] mb-3">Your invoice</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-ink leading-[1.15]">
              Enter your invoice number
            </h1>
            <div className="divider-thin" />
          </div>

          <div
            className="bg-white border border-line/60 rounded-3xl
              shadow-[0_8px_40px_rgba(44,26,29,0.04)] p-6 sm:p-8"
          >
            <p className="block overline text-[10px] mb-3">
              Invoice Number <span className="text-maroon">*</span>
            </p>
            <div
              data-testid="invoice-entry-input"
              className="flex items-center justify-between gap-2 sm:justify-start sm:gap-3"
            >
              {digits.map((digit, index) => (
                <input
                  key={index}
                  ref={(element) => {
                    digitRefs.current[index] = element;
                  }}
                  aria-label={`Invoice digit ${index + 1} of 4`}
                  autoComplete="off"
                  autoFocus={index === 0}
                  className="h-16 w-full max-w-[72px] rounded-xl border border-line bg-ivory/45
                    text-center font-serif text-2xl text-ink caret-maroon outline-none
                    transition-[border-color,box-shadow] focus:border-maroon focus:ring-2
                    focus:ring-maroon/15 sm:h-[72px]"
                  data-testid={`invoice-digit-${index}`}
                  inputMode="numeric"
                  maxLength={1}
                  onChange={(event) => handleDigitChange(event, index)}
                  onKeyDown={(event) => handleDigitKeyDown(event, index)}
                  onPaste={(event) => {
                    event.preventDefault();
                    applyPastedDigits(event.clipboardData.getData("text"), index);
                  }}
                  type="text"
                  value={digit}
                />
              ))}
            </div>
            {showError && (
              <p
                data-testid="invoice-entry-error"
                className="mt-2 text-[11px] text-[#8A2A2A] leading-relaxed"
              >
                {errorMessage}
              </p>
            )}

            <button
              type="button"
              onClick={() => setShowHelp((v) => !v)}
              className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-maroon
                hover:underline"
              data-testid="invoice-entry-help-btn"
            >
              <HelpCircle size={12} strokeWidth={1.6} />
              Where can I find this?
            </button>
            <AnimatePresence>
              {showHelp && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <p className="text-xs text-muted2 mt-2 leading-relaxed">
                    Your invoice number is printed at the top of your stitching invoice.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSampleOpen(true)}
                    data-testid="view-sample-invoice-btn"
                    className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-maroon
                      hover:underline font-medium"
                  >
                    View a sample invoice →
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            <button
              type="submit"
              data-testid="invoice-entry-continue"
              disabled={!invoiceValid}
              className="btn-maroon w-full mt-8 text-base disabled:opacity-40
                disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            >
              Continue
              <ArrowRight size={16} strokeWidth={1.7} />
            </button>
          </div>
        </motion.form>
      </main>

      <SampleInvoiceModal open={sampleOpen} onClose={() => setSampleOpen(false)} />
    </div>
  );
}
